import type { UserDto } from '../dto/user.dto';

/** The site a session is for; the server keeps Books sessions to system admins. */
export type SessionApp = 'marketedge' | 'books';

/** What every server auth endpoint answers with. */
export interface AuthResponse {
    success: boolean;
    message?: string;
    access_token?: string;
    refresh_token?: string;
    user?: UserDto;
}

export interface TokenSessionOptions {
    /** Prepended to every relative API path: '' for a same-origin app. */
    apiBase: string;
    app: SessionApp;
    /** localStorage key prefix, so two apps on one origin never share tokens. */
    storagePrefix: string;
    /** The signed-in user changed (null = signed out). */
    onUser?: (user: UserDto | null) => void;
}

/**
 * A browser login against the MarketEdge server's /market-edge/auth API,
 * with no framework in it: token storage, one refresh at a time, and the
 * retry-once-on-401 that every API call needs. Books wraps it in a Svelte
 * store; it mirrors what client/src/lib/stores/auth.ts does for MarketEdge.
 */
export class TokenSession {
    private refreshInFlight: Promise<string | null> | null = null;

    constructor(private readonly options: TokenSessionOptions) {}

    private get accessKey(): string {
        return this.options.storagePrefix + 'access_token';
    }

    private get refreshKey(): string {
        return this.options.storagePrefix + 'refresh_token';
    }

    private read(key: string): string | null {
        try {
            return localStorage.getItem(key);
        } catch {
            return null;
        }
    }

    hasTokens(): boolean {
        return this.read(this.refreshKey) != null;
    }

    private store(data: AuthResponse): void {
        if (!data.access_token || !data.refresh_token) return;
        localStorage.setItem(this.accessKey, data.access_token);
        localStorage.setItem(this.refreshKey, data.refresh_token);
        this.options.onUser?.(data.user ?? null);
    }

    /** Forgets the tokens without telling the server (it already said no). */
    clear(): void {
        try {
            localStorage.removeItem(this.accessKey);
            localStorage.removeItem(this.refreshKey);
        } catch {
            // storage blocked: nothing was stored either
        }
        this.options.onUser?.(null);
    }

    private url(path: string): string {
        return this.options.apiBase + path.replace(/^\//, '');
    }

    private async post(path: string, body: Record<string, unknown>): Promise<AuthResponse> {
        const response = await fetch(this.url(path), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
        try {
            return await response.json() as AuthResponse;
        } catch {
            return { success: false, message: `Sign-in service answered ${response.status}` };
        }
    }

    async sendOtpEmail(email: string): Promise<AuthResponse> {
        return this.post('market-edge/auth/send-otp-email', { email, app: this.options.app });
    }

    async sendOtpSms(phone: string): Promise<AuthResponse> {
        return this.post('market-edge/auth/send-otp-sms', { phone, app: this.options.app });
    }

    async verifyOtp(identifier: { email?: string; phone?: string }, otp: string): Promise<AuthResponse> {
        const data = await this.post('market-edge/auth/verify-otp', { ...identifier, otp, app: this.options.app });
        if (data.success) this.store(data);
        return data;
    }

    /** Development only: the server's DEV_AUTO_LOGIN user. */
    async devLogin(): Promise<AuthResponse> {
        const data = await this.post('market-edge/auth/dev-login', { app: this.options.app });
        if (data.success) this.store(data);
        return data;
    }

    /**
     * New tokens from the refresh token. Concurrent callers share one
     * request. Resolves to the new access token, or null when the session is
     * gone (the tokens are then cleared).
     */
    async refresh(): Promise<string | null> {
        if (this.refreshInFlight) return this.refreshInFlight;
        this.refreshInFlight = (async () => {
            const refreshToken = this.read(this.refreshKey);
            if (!refreshToken) return null;
            const data = await this.post('market-edge/auth/request-access-token', { refresh_token: refreshToken });
            if (!data.success || !data.access_token) {
                this.clear();
                return null;
            }
            this.store(data);
            return data.access_token;
        })().finally(() => {
            this.refreshInFlight = null;
        });
        return this.refreshInFlight;
    }

    /**
     * fetch() with the bearer token, refreshing once on a 401. A second 401
     * means the session is over: the tokens are cleared and the 401 returned.
     */
    async fetch(path: string, init: RequestInit = {}): Promise<Response> {
        const send = (token: string | null) => {
            const headers = new Headers(init.headers);
            if (token) headers.set('Authorization', `Bearer ${token}`);
            return fetch(this.url(path), { ...init, headers });
        };
        let response = await send(this.read(this.accessKey));
        if (response.status === 401) {
            const token = await this.refresh();
            if (!token) return response;
            response = await send(token);
            if (response.status === 401) this.clear();
        }
        return response;
    }

    async logout(): Promise<void> {
        try {
            await this.fetch('market-edge/auth/logout', { method: 'POST' });
        } finally {
            this.clear();
        }
    }
}
